"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import { FiCopy } from "react-icons/fi";
import { handleCopy } from "@/components/common/commonFunction/handleCopy";
import { rgbToHex, rgbToHsl } from "@/components/developmentFormat/colorPickerConverter/colorUtils";
import styles from "./ImageColorPicker.module.css";

const ACCEPT = "image/*";
const MAX_DISPLAY_SIZE = 560;

type PickedColor = { r: number; g: number; b: number };

function getPixelFromImage(
  img: HTMLImageElement,
  x: number,
  y: number
): PickedColor | null {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  const px = Math.floor(x);
  const py = Math.floor(y);
  if (px < 0 || py < 0 || px >= canvas.width || py >= canvas.height) return null;
  const data = ctx.getImageData(px, py, 1, 1).data;
  return { r: data[0], g: data[1], b: data[2] };
}

function extractPalette(img: HTMLImageElement, count: number = 8): PickedColor[] {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0);
  const w = canvas.width;
  const h = canvas.height;
  const data = ctx.getImageData(0, 0, w, h).data;
  type Bucket = { r: number; g: number; b: number; n: number };
  const bucket = new Map<string, Bucket>();
  const step = Math.max(1, Math.floor((w * h) / 4000));
  for (let i = 0; i < data.length; i += 4 * step) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = `${Math.round(r / 16) * 16},${Math.round(g / 16) * 16},${Math.round(b / 16) * 16}`;
    const cur = bucket.get(key);
    if (cur) {
      cur.n += 1;
      cur.r = (cur.r * (cur.n - 1) + r) / cur.n;
      cur.g = (cur.g * (cur.n - 1) + g) / cur.n;
      cur.b = (cur.b * (cur.n - 1) + b) / cur.n;
    } else {
      bucket.set(key, { r, g, b, n: 1 });
    }
  }
  return [...bucket.values()]
    .sort((a, b) => b.n - a.n)
    .slice(0, count)
    .map(({ r, g, b }) => ({ r: Math.round(r), g: Math.round(g), b: Math.round(b) }));
}

export default function ImageColorPicker() {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pickedColor, setPickedColor] = useState<PickedColor | null>(null);
  const [palette, setPalette] = useState<PickedColor[]>([]);
  const [copied, setCopied] = useState<"hex" | "rgb" | "hsl" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const revokeUrl = useCallback((url: string | null) => {
    if (url) try { URL.revokeObjectURL(url); } catch {}
  }, []);

  useEffect(() => {
    return () => {
      if (imageUrl) revokeUrl(imageUrl);
    };
  }, [imageUrl, revokeUrl]);

  useEffect(() => {
    if (copied) {
      const t = setTimeout(() => setCopied(null), 2000);
      return () => clearTimeout(t);
    }
  }, [copied]);

  const onFile = useCallback(
    (file: File | null) => {
      setError(null);
      setPickedColor(null);
      setPalette([]);
      if (imageUrl) {
        revokeUrl(imageUrl);
        setImageUrl(null);
      }
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setError("Please choose an image file (e.g. JPEG, PNG, WebP).");
        return;
      }
      setImageUrl(URL.createObjectURL(file));
    },
    [imageUrl, revokeUrl]
  );

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    setPalette(extractPalette(img, 8));
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      onFile(e.dataTransfer?.files?.[0] ?? null);
    },
    [onFile]
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const hex = pickedColor ? rgbToHex(pickedColor.r, pickedColor.g, pickedColor.b) : "";
  const hsl = pickedColor ? rgbToHsl(pickedColor.r, pickedColor.g, pickedColor.b) : null;
  const rgbStr = pickedColor ? `rgb(${pickedColor.r}, ${pickedColor.g}, ${pickedColor.b})` : "";
  const hslStr = hsl !== null ? `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : "";

  const onCopy = (type: "hex" | "rgb" | "hsl", text: string) => {
    handleCopy(text, () => setCopied(type));
  };

  return (
    <div className="children-box-alignment">
      <div className={styles.wrapper}>
        <h2 className={styles.title}>Image Color Picker</h2>
        <p className={styles.subtitle}>
          Upload an image and click any pixel to get its color in HEX, RGB, and HSL.
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <div
          className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ""} ${imageUrl ? styles.dropZoneHidden : ""}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <input
            type="file"
            accept={ACCEPT}
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            className={styles.fileInput}
            aria-label="Upload image"
          />
          <div className={styles.dropContent}>
            <span className={styles.dropIcon}>↑</span>
            <p>Drag an image here or <strong>click to browse</strong></p>
          </div>
        </div>

        {imageUrl && (
          <>
            <div className={styles.pickerSection}>
              <CanvasDraw
                imageUrl={imageUrl}
                maxSize={MAX_DISPLAY_SIZE}
                onPick={setPickedColor}
                onImageLoad={onImageLoad}
              />
              <p className={styles.hintClick}>Click anywhere on the image to pick a color.</p>
            </div>

            {palette.length > 0 && (
              <div className={styles.paletteSection}>
                <h3 className={styles.paletteTitle}>Palette from image</h3>
                <div className={styles.paletteGrid}>
                  {palette.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      className={styles.paletteSwatch}
                      style={{ backgroundColor: rgbToHex(c.r, c.g, c.b) }}
                      onClick={() => setPickedColor(c)}
                      title={rgbToHex(c.r, c.g, c.b)}
                      aria-label={`Color ${i + 1}: ${rgbToHex(c.r, c.g, c.b)}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {pickedColor && (
              <div className={styles.resultSection}>
                <h3 className={styles.resultTitle}>Picked color</h3>
                <div className={styles.resultRow}>
                  <div className={styles.swatch} style={{ backgroundColor: hex }} aria-hidden />
                  <div className={styles.codes}>
                    <div className={styles.codeRow}>
                      <label className={styles.codeLabel}>HEX</label>
                      <div className={styles.codeValue}>
                        <code>{hex}</code>
                        <button
                          type="button"
                          className={styles.copyBtn}
                          onClick={() => onCopy("hex", hex)}
                          title="Copy HEX"
                          aria-label="Copy HEX"
                        >
                          <FiCopy size={16} />
                          {copied === "hex" && <span className={styles.copied}>Copied</span>}
                        </button>
                      </div>
                    </div>
                    <div className={styles.codeRow}>
                      <label className={styles.codeLabel}>RGB</label>
                      <div className={styles.codeValue}>
                        <code>{rgbStr}</code>
                        <button
                          type="button"
                          className={styles.copyBtn}
                          onClick={() => onCopy("rgb", rgbStr)}
                          title="Copy RGB"
                          aria-label="Copy RGB"
                        >
                          <FiCopy size={16} />
                          {copied === "rgb" && <span className={styles.copied}>Copied</span>}
                        </button>
                      </div>
                    </div>
                    <div className={styles.codeRow}>
                      <label className={styles.codeLabel}>HSL</label>
                      <div className={styles.codeValue}>
                        <code>{hslStr}</code>
                        <button
                          type="button"
                          className={styles.copyBtn}
                          onClick={() => onCopy("hsl", hslStr)}
                          title="Copy HSL"
                          aria-label="Copy HSL"
                        >
                          <FiCopy size={16} />
                          {copied === "hsl" && <span className={styles.copied}>Copied</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CanvasDraw({
  imageUrl,
  maxSize,
  onPick,
  onImageLoad,
}: {
  imageUrl: string;
  maxSize: number;
  onPick: (c: PickedColor) => void;
  onImageLoad: (img: HTMLImageElement) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);
  const [displaySize, setDisplaySize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      let dw = w;
      let dh = h;
      if (w > h) {
        if (w > maxSize) {
          dw = maxSize;
          dh = (h / w) * maxSize;
        }
      } else {
        if (h > maxSize) {
          dh = maxSize;
          dw = (w / h) * maxSize;
        }
      }
      setDisplaySize({ w: Math.round(dw), h: Math.round(dh) });
      onImageLoad(img);

      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const offCtx = off.getContext("2d");
      if (offCtx) offCtx.drawImage(img, 0, 0);
      offscreenRef.current = off;
    };
    img.onerror = () => {
      imgRef.current = null;
    };
    img.src = imageUrl;
  }, [imageUrl, maxSize, onImageLoad]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !displaySize) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, displaySize.w, displaySize.h);
  }, [displaySize, imageUrl]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const off = offscreenRef.current;
    const canvas = canvasRef.current;
    if (!off || !canvas || !displaySize) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = off.width / rect.width;
    const scaleY = off.height / rect.height;
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    if (x < 0 || y < 0 || x >= off.width || y >= off.height) return;
    const ctx = off.getContext("2d");
    if (!ctx) return;
    const data = ctx.getImageData(x, y, 1, 1).data;
    onPick({ r: data[0], g: data[1], b: data[2] });
  };

  if (!displaySize) {
    return <div className={styles.loading}>Loading image…</div>;
  }

  return (
    <canvas
      ref={canvasRef}
      width={displaySize.w}
      height={displaySize.h}
      className={styles.canvas}
      style={{
        width: displaySize.w,
        height: displaySize.h,
        maxWidth: "100%",
        cursor: "crosshair",
      }}
      onClick={handleClick}
      aria-label="Click to pick a color"
    />
  );
}
